import React from 'react'

import { withStyles } from '@material-ui/core/styles'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import LockIcon from '@mui/icons-material/LockOpen'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import { setLayerPanelsClosed, setLayerPanelsOpen, setLayerSelected, setLayerUnselected } from 'drawer/tabs/layers/actions'
import LayerHeaderCheckbox from 'drawer/tabs/layers/panels/LayerHeaderCheckbox'
import IsMapZoomedToLayerMinZoom from 'drawer/tabs/layers/utils/IsMapZoomedToLayerMinZoom'

import LayerContextMenu from './LayerContextMenu'
import LayerLegend from './LayerLegend'
import LayerLoadStatus from './LayerLoadStatus'

const styles = {
  root: {
    '&:hover': {
      backgroundColor: '#f0f0f0'
    }
  },
  filtered: {
    padding: 0
  },
  filteredHidden: {
    display: 'none'
  },
  filteredIcon: {
    maxWidth: 16,
    opacity: 0.54
  },
  table: {
    border: 0,
    minHeight: 32, // align with '& .ant-tree-treenode' in LayerPanels
    '& td': {
      border: 'none'
    }
  },
  body: {
    border: 0
  },
  row: {
    border: 0
  },
  checkbox: {
    border: 0,
    width: 20,
    padding: 0,
    textAlign: 'left',
    verticalAlign: 'top'
  },
  titleEnabled: {
    cursor: 'pointer',
    fontSize: 15
  },
  titleDisabled: {
    cursor: 'pointer',
    fontSize: 15,
    color: 'grey'
  },
  titleSelected: {
    cursor: 'pointer',
    border: 0,
    fontSize: 15,
    fontWeight: 'bold',
    padding: 0
  },
  titleUnselected: {
    cursor: 'pointer',
    border: 0,
    fontSize: 15,
    padding: 0
  },
  fillerCell: {
    padding: 0,
    width: 10
  },
  loadstatusCell: {
    border: 'none',
    width: 20
  },
  lockIconRoot: {
    maxWidth: 16,
    opacity: 0.54
  },
  lockIconCell: {
    border: 0,
    padding: 0,
    width: 30,
    textAlign: 'center'
  },
  contextMenu: {
    border: 0,
    width: 30,
    padding: 0,
    textAlign: 'right'
  },
  legend: {
    // Adjust legend table (icon styles) to align with layer title
    '& table': {
      marginLeft: -3
    }
  }
}

// https://material-ui.com/api/table-cell/
const tableCellPaddingProp = 'none' // enum: 'default', 'checkbox', 'dense', 'none'
const tableCellAlignPropRight = 'right' // enum: 'inherit', 'left', 'center', 'right', 'justify'

class LayerHeader_ extends React.Component {
  constructor (props) {
    super(props)

    // Pre-build static UI elements to avoid costly re-renders. e.g. icons, buttons, tooltips, etc.
    this.pure = {}
    this.pure.lockIcon = this.lockIcon()
    this.pure.titleTextEnabled = this.titleText(true) // Must be after this.toggleLayer
    this.pure.titleTextDisabled = this.titleText(false) // Must be after this.toggleLayer
    this.pure.tooltipMinZoom = 'Layer not available at this zoom level' // + minZoom
  }

  lockIcon = () => {
    const { classes } = this.props

    return (
      <TableCell onClick={this.toggleLayer} className={classes.lockIconCell} padding={tableCellPaddingProp} align={tableCellAlignPropRight}>
        <LockIcon classes={{ root: classes.lockIconRoot }} />
      </TableCell>
    )
  }

  titleText = (bEnabled) => {
    const { classes, layerTitle } = this.props

    let titleClass = classes.titleDisabled
    if (bEnabled) {
      titleClass = classes.titleEnabled
    }

    return (
      <TableCell onClick={this.toggleLayer} className={classes.titleUnselected} padding={tableCellPaddingProp}>
        <Typography className={titleClass}>{layerTitle}</Typography>
      </TableCell>
    )
  }

  toggleLayer = () => {
    const { layerSelected, mapZ, minZoom, dispatchSetLayerSelected, dispatchSetLayerUnselected, titleid } = this.props

    if (!IsMapZoomedToLayerMinZoom(mapZ, minZoom)) {
      return
    }

    if (layerSelected) {
      dispatchSetLayerUnselected(titleid)
    } else {
      dispatchSetLayerSelected(titleid)
    }
  }

  togglePanels = () => {
    const {
      dispatchSetLayerPanelsOpen,
      dispatchSetLayerPanelsClosed,
      dispatchSetLayerSelected,
      layerPanelsOpen,
      layerSelected,
      titleid
    } = this.props

    if (!layerSelected) {
      dispatchSetLayerSelected(titleid)
      dispatchSetLayerPanelsOpen(titleid)
    } else if (!layerPanelsOpen) {
      dispatchSetLayerPanelsOpen(titleid)
    } else {
      dispatchSetLayerPanelsClosed(titleid)
    }
  }

  render () {
    const { classes, titleid, showInLayerControl, layerFiltered, layerSelected, localDataSource, mapZ, minZoom } = this.props

    if (showInLayerControl === false) {
      return null
    }

    // console.log('Render layer', titleid, 'header')

    let toolTip = ''
    let titleText = null
    const bEnableLayer = IsMapZoomedToLayerMinZoom(mapZ, minZoom) // TODO: Move this to a redux action.
    if (bEnableLayer) {
      titleText = this.pure.titleTextEnabled
    } else {
      toolTip = this.pure.tooltipMinZoom
      titleText = this.pure.titleTextDisabled
    }

    let lockIcon = null
    if (localDataSource) {
      lockIcon = this.pure.lockIcon
    }

    // Check if layer is filtered, if it is hide the icon
    const filteredClass = layerFiltered ? classes.filtered : classes.filteredHidden
    const filteredToolTip = 'This layer has a filter applied'

    // console.debug('LayerTree - nodeTitleRenderer - layer, filter', titleid, layerFiltered)

    return (
      <div className={classes.root}>
        <Tooltip title={toolTip} disableTouchListener={true} disableFocusListener={true}>
          <Table className={classes.table}>
            <TableBody className={classes.body}>
              <TableRow className={classes.row} key={titleid}>
                <TableCell className={classes.checkbox} padding={tableCellPaddingProp}>
                  <LayerHeaderCheckbox titleid={titleid} toggleLayer={this.toggleLayer}/>
                </TableCell>
                {titleText}
                <TableCell className={classes.loadstatusCell} padding={tableCellPaddingProp}>
                  <LayerLoadStatus titleid={titleid} />
                </TableCell>
                <TableCell className={filteredClass}>
                  <Tooltip title={filteredToolTip}>
                    <FilterAltIcon className={classes.filteredIcon} />
                  </Tooltip>
                </TableCell >
                {lockIcon}
                <TableCell className={classes.contextMenu} align={tableCellAlignPropRight}>
                  <LayerContextMenu titleid={titleid} show={layerSelected} />
                </TableCell>
                <TableCell className={classes.fillerCell} />
              </TableRow>
              <TableRow className={classes.row} key={`${titleid}_legend`}>
                <TableCell className={classes.checkbox} padding={tableCellPaddingProp}>
                  {/* Dummy cell to make legend align with title */}
                </TableCell>
                <TableCell className={classes.legend} padding={tableCellPaddingProp} colSpan={2}>
                  { layerSelected && <LayerLegend titleid={titleid} /> }
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Tooltip>
      </div>
    )
  }
}

LayerHeader_.propTypes = {
  classes: PropTypes.object.isRequired,
  titleid: PropTypes.string.isRequired,
  layerFiltered: PropTypes.bool.isRequired,
  layerTitle: PropTypes.string.isRequired,
  layerPanelsOpen: PropTypes.bool.isRequired,
  layerSelected: PropTypes.bool.isRequired,
  showInLayerControl: PropTypes.bool.isRequired,
  localDataSource: PropTypes.bool.isRequired,
  selectorType: PropTypes.string.isRequired,
  minZoom: PropTypes.number.isRequired,
  dispatchSetLayerSelected: PropTypes.func.isRequired,
  dispatchSetLayerUnselected: PropTypes.func.isRequired,
  dispatchSetLayerPanelsOpen: PropTypes.func.isRequired,
  dispatchSetLayerPanelsClosed: PropTypes.func.isRequired,
  mapZ: PropTypes.number.isRequired
}

function mapStateToProps (state, ownProps) {
  const { titleid } = ownProps

  const layer = state.layers[titleid]

  const layerFiltered = !!layer?.propsFilter?.value
  const layerTitle = layer?.title ?? ''
  const layerSelected = layer?.selected ?? false
  const layerPanelsOpen = layer?.panelsOpen ?? false
  const showInLayerControl = layer?.showInLayerControl ?? true
  const localDataSource = layer?.localDataSource ?? false
  const selectorType = layer?.selectorType ?? ''
  const minZoom = layer?.minZoom ?? 0
  const mapZ = state.mapZ ?? 0

  return {
    layerFiltered,
    layerPanelsOpen,
    layerSelected,
    layerTitle,
    localDataSource,
    mapZ,
    minZoom,
    selectorType,
    showInLayerControl
  }
}

const mapDispatchToProps = dispatch => {
  return {
    dispatchSetLayerSelected: titleid => {
      dispatch(setLayerSelected(titleid))
    },
    dispatchSetLayerUnselected: titleid => {
      dispatch(setLayerUnselected(titleid))
    },
    dispatchSetLayerPanelsOpen: titleid => {
      dispatch(setLayerPanelsOpen(titleid))
    },
    dispatchSetLayerPanelsClosed: titleid => {
      dispatch(setLayerPanelsClosed(titleid))
    }
  }
}

const LayerHeader = (
  withStyles(styles)(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(LayerHeader_)
  )
)

export default LayerHeader
